import React from 'react';
import { Plus, Warehouse, MapPin, Users, Edit, Trash2, AlertTriangle, XCircle, CheckCircle, Archive, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { Th, Td } from '../WMSShared';

export default function InventoryTab({ warehouses, alerts, setEditItem, setForm, setModal, delWarehouse, cur = 'S$',
  purchases = [], products = [], setPurchaseItems, API, switchTab }) {
  const [expandedPO, setExpandedPO] = React.useState(null);

  const whName = (id) => warehouses.find(w => w.warehouse_id === id)?.name || id;
  const prodName = (pid, sku) => {
    if (pid) { const p = products.find(x => x.product_id === pid); if (p) return p.name; }
    return sku || pid || '—';
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Warehouses & Inventory</h2>
        <div className="flex gap-2">
          <button onClick={() => { setEditItem(null); setForm({ warehouse_id: warehouses[0]?.warehouse_id || '' }); setPurchaseItems([]); setModal('purchase'); }}
            className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-600 shadow-sm">
            <Plus className="w-4 h-4" />Receive New Stock
          </button>
          <button onClick={() => { setEditItem(null); setForm({ capacity: 1000 }); setModal('warehouse'); }}
            className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 shadow-sm">
            <Plus className="w-4 h-4" />Add Warehouse
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {warehouses.map(wh => (
          <div key={wh.warehouse_id} className="bg-white rounded-xl p-5 border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Warehouse className="w-5 h-5 text-blue-600" /></div>
                <div><h3 className="font-semibold">{wh.name}</h3><p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{wh.location}</p></div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditItem(wh); setForm(wh); setModal('warehouse'); }} className="p-1 text-gray-400 hover:text-blue-500"><Edit className="w-4 h-4" /></button>
                <button onClick={() => delWarehouse(wh.warehouse_id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {wh.manager && <p className="text-xs text-gray-500 mb-3 flex items-center gap-1"><Users className="w-3 h-3" />{wh.manager}</p>}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Products</span><span className="font-medium">{wh.product_count}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Items</span><span className="font-medium">{wh.total_items}</span></div>
              <div>
                <div className="flex justify-between mb-1"><span className="text-gray-500">Capacity</span><span className="font-medium">{wh.utilization}%</span></div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${wh.utilization > 80 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${wh.utilization}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {warehouses.length === 0 && <div className="col-span-3 text-center py-12 text-gray-400">No warehouses. Add one to start.</div>}
      </div>
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-5 py-4 border-b"><h3 className="font-semibold">Stock Alerts ({alerts.length})</h3></div>
        <div className="divide-y">
          {alerts.map(p => (
            <button key={p.product_id || p.sku} onClick={() => switchTab && switchTab('agent')}
              className="flex items-center justify-between px-5 py-3 w-full text-left hover:bg-blue-50 transition-colors cursor-pointer group"
              data-testid={`stock-alert-${p.sku}`}>
              <div className="flex items-center gap-3">
                {p.stock === 0 ? <XCircle className="w-5 h-5 text-red-500" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                <div><p className="text-sm font-medium group-hover:text-blue-700 transition-colors">{p.name}</p><p className="text-xs text-gray-500">{p.sku} - {p.warehouse_id}</p></div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold ${p.stock === 0 ? 'text-red-600' : 'text-amber-600'}`}>{p.stock === 0 ? 'Out of stock' : `${p.stock} left`}</span>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
              </div>
            </button>
          ))}
          {alerts.length === 0 && (
            <div className="p-8 text-center text-gray-400"><CheckCircle className="w-8 h-8 mx-auto mb-2" /><p className="text-sm">All stock levels healthy</p></div>
          )}
        </div>
      </div>

      {/* Purchase Orders (Receive Stock history) */}
      {purchases.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden mt-6">
          <div className="px-5 py-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-700">Purchase Orders ({purchases.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><th className="w-8"></th><Th>PO Number</Th><Th>Supplier</Th><Th>Products</Th><Th>Total</Th><Th>Warehouse</Th><Th>Date</Th><Th>Actions</Th></tr></thead>
              <tbody className="divide-y">
                {purchases.map(po => {
                  const items = po.items || [];
                  const isOpen = expandedPO === po.purchase_id;
                  const summary = items.length === 1
                    ? `${items[0].quantity}× ${prodName(items[0].product_id, items[0].sku) || items[0].name}`
                    : `${items.length} products (${po.total_items} units)`;
                  return (
                    <React.Fragment key={po.purchase_id}>
                      <tr className="hover:bg-gray-50">
                        <Td>{items.length > 1 && <button onClick={() => setExpandedPO(isOpen ? null : po.purchase_id)} className="p-1 text-gray-400 hover:text-gray-600">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>}</Td>
                        <Td className="font-mono text-xs">{po.purchase_id}</Td>
                        <Td>{po.supplier || '—'}</Td>
                        <Td className="max-w-[200px] truncate">{summary}</Td>
                        <Td className="font-semibold text-emerald-700">{cur}{po.total_cost?.toLocaleString()}</Td>
                        <Td className="text-xs text-gray-500">{whName(po.warehouse_id)}</Td>
                        <Td className="text-xs text-gray-400">{po.created_at?.split('T')[0]}</Td>
                        <Td><div className="flex gap-1">
                          <button onClick={() => window.open(`${API}/docs/po/${po.purchase_id}/html`, '_blank')} className="text-xs border px-2 py-1 rounded hover:bg-gray-50">HTML</button>
                          <button onClick={() => window.open(`${API}/docs/po/${po.purchase_id}/pdf`, '_blank')} className="text-xs border px-2 py-1 rounded hover:bg-gray-50">PDF</button>
                        </div></Td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-blue-50/20"><td colSpan={8} className="px-8 py-3">
                          <table className="w-full text-xs"><thead><tr className="text-gray-400"><th className="text-left pb-1 w-1/2">Product</th><th className="text-left pb-1">SKU</th><th className="text-right pb-1">Qty</th><th className="text-right pb-1">Unit Cost</th><th className="text-right pb-1">Total</th></tr></thead>
                          <tbody>{items.map((item, idx) => (<tr key={idx}><td className="py-1 font-medium">{prodName(item.product_id, item.sku) || item.name}</td><td className="py-1 text-gray-400 font-mono">{item.sku}</td><td className="py-1 text-right">{item.quantity}</td><td className="py-1 text-right text-gray-500">{cur}{item.unit_cost}</td><td className="py-1 text-right font-semibold text-emerald-700">{cur}{((item.unit_cost||0)*(item.quantity||0)).toFixed(2)}</td></tr>))}</tbody>
                          </table>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
